import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Icon, PrimaryButton, SecondaryButton, InlineTextButton } from '../../components';
import classNames from 'classnames';

import {
  CONSENT_CATEGORIES,
  DEFAULT_PREFERENCES,
  OPTIONAL_CATEGORIES,
} from '../../util/cookieConsent';
import { loadConsent, saveConsent, reopenConsentHandled } from '../../ducks/cookieConsent.duck';

import css from './CookieConsent.module.css';

/**
 * Cookie consent banner.
 *
 * Two things changed here beyond the obvious.
 *
 * The decision now goes through the cookieConsent duck rather than straight to
 * `document.cookie`, because other components have to be able to read it. It
 * previously wrote a cookie that only this component ever looked at, which is
 * why choosing "Essential only" changed nothing at all.
 *
 * The page blacklist is gone. It hid the banner on HelpPage, TopicPage and
 * ArticlePage, which are the only three pages where Intercom mounts. So the one
 * third party we control loaded on exactly the pages where we never asked. The
 * banner now shows everywhere, and Intercom waits for an answer.
 *
 * Categories match what Attractr actually runs. The Marketing toggle is gone:
 * there are no advertising tags in the product, and offering a switch for
 * tracking we do not do both misleads the visitor and puts a grant on file for
 * something that never happens.
 */
const categoryCopy = {
  [CONSENT_CATEGORIES.FUNCTIONAL]: {
    title: 'CookieConsent.functionalTitle',
    // Names the support chat actually in use (SupportChatWidget).
    description: process.env.REACT_APP_BREVO_CONVERSATIONS_ID
      ? 'CookieConsent.functionalDescriptionBrevo'
      : 'CookieConsent.functionalDescription',
  },
  [CONSENT_CATEGORIES.STATISTICS]: {
    title: 'CookieConsent.statisticsTitle',
    description: 'CookieConsent.statisticsDescription',
  },
};

export class CookieConsentComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showPreferences: false,
      preferences: { ...DEFAULT_PREFERENCES },
    };

    this.onAcceptAll = this.onAcceptAll.bind(this);
    this.onAcceptNecessary = this.onAcceptNecessary.bind(this);
    this.onSavePreferences = this.onSavePreferences.bind(this);
    this.togglePreferences = this.togglePreferences.bind(this);
    this.handlePreferenceChange = this.handlePreferenceChange.bind(this);
  }

  componentDidMount() {
    // Reading the cookie is the app's first act, not just this banner's: every
    // consumer of consent reads the result out of redux.
    this.props.onLoadConsent();
  }

  componentDidUpdate(prevProps) {
    // Reopened from the footer: start the draft from what they chose last time
    // rather than from the all-off default, so "Cookie settings" shows their
    // current position instead of silently proposing a withdrawal.
    if (this.props.reopenRequested && !prevProps.reopenRequested) {
      const current = this.props.consent;
      this.setState({
        showPreferences: true,
        preferences: current ? { ...current } : { ...DEFAULT_PREFERENCES },
      });
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  save(preferences) {
    this.props.onSaveConsent(preferences);
    this.setState({ showPreferences: false });
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  onAcceptAll() {
    this.save(
      OPTIONAL_CATEGORIES.reduce((acc, category) => ({ ...acc, [category]: true }), {
        ...DEFAULT_PREFERENCES,
      })
    );
  }

  onAcceptNecessary() {
    this.save({ ...DEFAULT_PREFERENCES });
  }

  onSavePreferences() {
    this.save(this.state.preferences);
  }

  togglePreferences() {
    this.setState(prevState => {
      const showPreferences = !prevState.showPreferences;
      if (typeof document !== 'undefined') {
        document.body.style.overflow = showPreferences ? 'hidden' : '';
      }
      return { showPreferences };
    });
    if (this.props.reopenRequested) {
      this.props.onReopenHandled();
    }
  }

  handlePreferenceChange(category) {
    if (category === CONSENT_CATEGORIES.ESSENTIAL) return;
    this.setState(prevState => ({
      preferences: {
        ...prevState.preferences,
        [category]: !prevState.preferences[category],
      },
    }));
  }

  render() {
    const { className, rootClassName, consent, loaded, reopenRequested } = this.props;
    const { showPreferences, preferences } = this.state;

    // The server has no cookies to read, and rendering the banner there would
    // flash it for people who already answered.
    if (typeof window === 'undefined') return null;

    // Ask once, then only when they come back to change their mind.
    const needsAnswer = loaded && consent === null;
    if (!needsAnswer && !reopenRequested && !showPreferences) return null;

    const classes = classNames(rootClassName || css.root, className);

    if (showPreferences) {
      return (
        <div className={css.overlay}>
          <div className={css.preferencesModal}>
            <div className={css.preferencesHeader}>
              <h2 className={css.preferencesTitle}>
                <FormattedMessage id="CookieConsent.preferencesTitle" />
              </h2>
              <button
                className={css.closeButton}
                onClick={this.togglePreferences}
                aria-label={this.props.intl.formatMessage({ id: 'CookieConsent.close' })}
              >
                <Icon name="close" size={24} className={css.closeIcon} />
              </button>
            </div>

            <div className={css.preferencesContent}>
              {/* Essential: shown, never switchable. Without it there is no
                  session, no checkout and no record of this decision. */}
              <div className={css.categoryItem}>
                <span className={css.checkboxRoot}>
                  <input
                    type="checkbox"
                    id="cookie-essential"
                    checked
                    disabled
                    readOnly
                    className={css.checkboxInput}
                  />
                  <label htmlFor="cookie-essential" className={css.checkboxLabel}>
                    <span className={css.checkboxWrapper}>
                      <span className={css.box}>
                        <Icon name="check" size={14} />
                      </span>
                    </span>
                    <span className={css.categoryName}>
                      <FormattedMessage id="CookieConsent.essentialTitle" />
                    </span>
                  </label>
                  <span className={css.alwaysOn}>
                    <FormattedMessage id="CookieConsent.alwaysOn" />
                  </span>
                </span>
                <p className={css.categoryDescription}>
                  <FormattedMessage id="CookieConsent.essentialDescription" />
                </p>
              </div>

              {OPTIONAL_CATEGORIES.map(category => {
                const copy = categoryCopy[category];
                const inputId = `cookie-${category}`;
                return (
                  <label key={category} htmlFor={inputId} className={css.categoryItem}>
                    <span className={css.checkboxRoot}>
                      <input
                        type="checkbox"
                        id={inputId}
                        checked={!!preferences[category]}
                        onChange={() => this.handlePreferenceChange(category)}
                        className={css.checkboxInput}
                      />
                      <span className={css.checkboxLabel}>
                        <span className={css.checkboxWrapper}>
                          <span className={css.box}>
                            {preferences[category] ? <Icon name="check" size={14} /> : null}
                          </span>
                        </span>
                        <span className={css.categoryName}>
                          <FormattedMessage id={copy.title} />
                        </span>
                      </span>
                    </span>
                    <p className={css.categoryDescription}>
                      <FormattedMessage id={copy.description} />
                    </p>
                  </label>
                );
              })}
            </div>

            <div className={css.preferencesFooter}>
              <PrimaryButton className={css.saveButton} onClick={this.onSavePreferences}>
                <FormattedMessage id="CookieConsent.savePreferences" />
              </PrimaryButton>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={classes}>
        <div className={css.bannerContent}>
          <div className={css.textContent}>
            <h2 className={css.title}>
              <FormattedMessage id="CookieConsent.title" />
            </h2>
            <p className={css.message}>
              <FormattedMessage id="CookieConsent.message" />
            </p>
          </div>

          {/* Order: Essential only (secondary) → Accept all (primary) →
              Manage preferences (inline link). Matches the spec from
              the client and keeps the safer default (essentials) as the
              leftmost button instead of accept-all. */}
          <div className={css.buttonGroup}>
            <SecondaryButton className={css.necessaryButton} onClick={this.onAcceptNecessary}>
              <FormattedMessage id="CookieConsent.onlyNecessary" />
            </SecondaryButton>
            <PrimaryButton className={css.acceptAllButton} onClick={this.onAcceptAll}>
              <FormattedMessage id="CookieConsent.acceptAll" />
            </PrimaryButton>
            <InlineTextButton
              rootClassName={css.preferencesButton}
              onClick={this.togglePreferences}
            >
              <FormattedMessage id="CookieConsent.managePreferences" />
            </InlineTextButton>
          </div>
        </div>
      </div>
    );
  }
}

const { bool, func, object, string } = PropTypes;

CookieConsentComponent.defaultProps = {
  className: null,
  rootClassName: null,
  consent: null,
  loaded: false,
  reopenRequested: false,
};

CookieConsentComponent.propTypes = {
  className: string,
  rootClassName: string,
  consent: object,
  loaded: bool,
  reopenRequested: bool,
  onLoadConsent: func.isRequired,
  onSaveConsent: func.isRequired,
  onReopenHandled: func.isRequired,
};

const mapStateToProps = state => {
  const { consent, loaded, reopenRequested } = state.cookieConsent;
  return { consent, loaded, reopenRequested };
};

const mapDispatchToProps = dispatch => ({
  onLoadConsent: () => dispatch(loadConsent()),
  onSaveConsent: preferences => dispatch(saveConsent(preferences)),
  onReopenHandled: () => dispatch(reopenConsentHandled()),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(CookieConsentComponent);
